import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Save, ShoppingCart, Plus, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { OfferVisual } from "@/components/FlashOffer";

const EMPTY = { title: "", description: "", price: "", old_price: "", image_path: "", ends_at: "", stock: 10, active: true };
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const toForm = (o) => ({ ...EMPTY, ...o, ends_at: toLocalInput(o.ends_at), old_price: o.old_price ?? "", stock: o.stock ?? 0 });

const OrdersList = ({ orders }) => (
  <div className="bg-card border border-border rounded-2xl p-5" data-testid="offer-orders-list">
    <div className="flex items-center gap-2 font-heading font-bold mb-3"><ShoppingCart className="h-4 w-4 text-blue-600" />Commandes reçues ({orders.length})</div>
    {orders.length === 0 ? <p className="text-sm text-muted-foreground">Aucune commande pour l'instant.</p> : (
      <div className="divide-y divide-border max-h-80 overflow-y-auto">
        {orders.map((o) => (
          <div key={o.id} className="py-2.5 text-sm flex justify-between gap-3" data-testid={`offer-order-${o.id}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate">{o.garage_name || o.created_by_name} · {o.quantity} × {o.offer_title}</div>
              {o.message && <div className="text-muted-foreground truncate">« {o.message} »</div>}
              <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("fr-FR")}</div>
            </div>
            <div className="font-heading font-black text-brand-red shrink-0">{o.total} €</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const OfferList = ({ offers, selectedId, onSelect, onNew }) => (
  <div className="bg-card border border-border rounded-2xl p-4" data-testid="offer-list">
    <div className="flex items-center justify-between mb-3">
      <span className="font-heading font-bold">Offres ({offers.length})</span>
      <Button size="sm" onClick={onNew} data-testid="offer-new-btn" className="bg-blue-700 hover:bg-blue-800"><Plus className="h-4 w-4 mr-1" />Nouvelle offre</Button>
    </div>
    <div className="flex flex-wrap gap-2">
      {offers.length === 0 && <p className="text-sm text-muted-foreground">Aucune offre. Créez la première.</p>}
      {offers.map((o) => (
        <button key={o.id} onClick={() => onSelect(o)} data-testid={`offer-item-${o.id}`}
          className={`px-3 py-2 rounded-lg border text-left text-sm ${selectedId === o.id ? "border-blue-600 bg-blue-500/5" : "border-border hover:border-blue-300"}`}>
          <div className="font-semibold">{o.title}</div>
          <div className="text-xs text-muted-foreground">{o.price} € · stock {o.stock ?? 0}{!o.active && " · masquée"}</div>
        </button>
      ))}
    </div>
  </div>
);

export default function OfferAdmin() {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    const [o, r] = await Promise.all([api.get("/offers"), api.get("/offer-orders")]);
    setOffers(o.data); setOrders(r.data);
    return o.data;
  }, []);

  useEffect(() => {
    load().then((list) => { if (list[0]) { setEditId(list[0].id); setForm(toForm(list[0])); } }).catch(() => {});
  }, [load]);

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_path: data.path }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      title: form.title, description: form.description, image_path: form.image_path, active: form.active,
      price: parseFloat(form.price), old_price: form.old_price ? parseFloat(form.old_price) : null,
      stock: parseInt(form.stock, 10) || 0, ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
    };
    try {
      const { data } = editId ? await api.put(`/offers/${editId}`, payload) : await api.post("/offers", payload);
      setEditId(data.id);
      toast.success(editId ? "Offre mise à jour" : "Offre publiée");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editId || !window.confirm("Supprimer cette offre ?")) return;
    try {
      await api.delete(`/offers/${editId}`);
      toast.success("Offre supprimée");
      setEditId(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const preview = { ...form, id: "preview", price: parseFloat(form.price) || 0, old_price: parseFloat(form.old_price) || null, stock: parseInt(form.stock, 10) || 0 };

  return (
    <div className="space-y-6" data-testid="offer-admin">
      <OfferList offers={offers} selectedId={editId} onSelect={(o) => { setEditId(o.id); setForm(toForm(o)); }} onNew={() => { setEditId(null); setForm(EMPTY); }} />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
          <h3 className="font-heading font-bold text-lg" data-testid="offer-form-title">{editId ? "Modifier l'offre" : "Nouvelle offre flash"}</h3>
          <div><Label>Titre</Label><Input data-testid="offer-title-input" value={form.title} onChange={set("title")} placeholder="Kit embrayage Valeo -30%" className="mt-1.5" /></div>
          <div><Label>Description</Label><Textarea data-testid="offer-desc-input" value={form.description} onChange={set("description")} className="mt-1.5" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Prix promo TTC (€)</Label><Input data-testid="offer-price-input" type="number" value={form.price} onChange={set("price")} className="mt-1.5" /></div>
            <div><Label>Prix barré (€)</Label><Input data-testid="offer-old-price-input" type="number" value={form.old_price} onChange={set("old_price")} className="mt-1.5" /></div>
            <div><Label>Stock</Label><Input data-testid="offer-stock-input" type="number" min={0} value={form.stock} onChange={set("stock")} className="mt-1.5" /></div>
          </div>
          <div><Label>Fin de l'offre</Label><Input data-testid="offer-ends-input" type="datetime-local" value={form.ends_at} onChange={set("ends_at")} className="mt-1.5" /></div>
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="offer-image-file" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="offer-image-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="h-4 w-4 mr-2" />{form.image_path ? "Changer l'illustration" : "Ajouter une illustration"}</>}
            </Button>
            <label className="flex items-center gap-2 text-sm ml-auto">
              <Switch data-testid="offer-active-switch" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />Visible
            </label>
          </div>
          <div className="flex gap-2">
            <Button data-testid="offer-save-btn" onClick={save} disabled={saving || !form.title || !form.price} className="flex-1 h-11 bg-blue-700 hover:bg-blue-800">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />{editId ? "Enregistrer" : "Publier l'offre"}</>}
            </Button>
            {editId && <Button data-testid="offer-delete-btn" variant="outline" onClick={remove} className="h-11 border-red-500/40 text-red-600"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Aperçu côté garagiste</div>
            <OfferVisual offer={{ ...preview, title: preview.title || "Titre de l'offre" }}>
              <Button disabled className="px-8 h-12 bg-blue-700"><ShoppingCart className="h-5 w-5 mr-2" />Commander</Button>
            </OfferVisual>
          </div>
          <OrdersList orders={orders} />
        </div>
      </div>
    </div>
  );
}
